import type { ElementRef, MouseEvent } from 'react';
import { forwardRef } from 'react';
import { IconButton as MuiIconButton } from '@material-ui/core';

import { Tooltip } from '@admin/shared/components/tooltips/tooltip';

// properties
type IconButtonProps = {
  tooltip: string;
  children: JSX.Element | JSX.Element[] | string;
  disabled?: boolean;
  onClick?: (evt?: any) => void;
  className?: string;
  disableRipple?: boolean;
  tabIndex?: number;
  onMouseDown?: (event: MouseEvent<HTMLButtonElement>) => void;
  onMouseEnter?: (event: MouseEvent<HTMLButtonElement>) => void;
  onMouseLeave?: (event: MouseEvent<HTMLButtonElement>) => void;
  href?: string;
  classes?: Record<string, any>;
  disableFocusRipple?: boolean;
  color?: 'default' | 'inherit' | 'primary' | 'secondary';
  size?: 'small' | 'medium';
};

/**
 * Holds an icon button with a tooltip.
 *
 * @param {string} tooltip tooltip text
 * @param {JSX.Element} children icon button content
 * @param {boolean} disabled is disabled
 * @param {IconButtonProps} props
 * @param {ElementRef<any>} ref
 *
 * @returns {JSX.Element}
 */
const IconButton = (
  { tooltip, children, disabled = false, ...props }: IconButtonProps,
  ref: ElementRef<any>,
): JSX.Element => {
  return !disabled ? ( // enabled with ref and tooltip
    <Tooltip title={tooltip}>
      <MuiIconButton {...props} disabled={disabled} ref={ref as any}>
        {children}
      </MuiIconButton>
    </Tooltip>
  ) : (
    // disabled without ref and tooltip
    <MuiIconButton {...props} disabled={disabled}>
      {children}
    </MuiIconButton>
  );
};

// create component with ref
const IconButtonWithRef = forwardRef<ElementRef<any>, IconButtonProps>(IconButton);

export { IconButtonWithRef as IconButton };
